/**
 * A valuation's charts - in the Valuation discipline, where they belong.
 *
 * They used to lead IAS accounting's Charts tab, drawn from an accounting model's
 * forecast; a valuation project had none. Every figure here is the valuation's own:
 * its cash flows, its bridge from enterprise value to equity, its other methods, and a
 * grid of full revaluations at neighbouring rates (computed by the engine, so the
 * centre cell IS the valuation and the chart cannot disagree with the overview).
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ReactNode } from 'react';

import { axisProps, fmtAxis, gridProps, seriesColor, tooltipProps } from '../../../charts/tokens';

type Row = Record<string, unknown>;

interface Sensitivity {
  discountRates: number[];
  growthRates: number[];
  equityValues: (number | null)[][];
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function money(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function pct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function Card({ title, note, children, wide }: { title: string; note: string; children: ReactNode; wide?: boolean }) {
  return (
    <section className={`rounded-xl border border-gray-200 bg-white p-5 ${wide ? 'lg:col-span-2' : ''}`}>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="mt-0.5 text-xs text-gray-500">{note}</p>
      <div className="mt-4">{children}</div>
    </section>
  );
}

/** Each year's free cash flow, built from EBITDA, and what it is worth today. */
function CashFlowChart({ rows }: { rows: Row[] }) {
  const data = rows.map((row) => ({
    year: String(row.year),
    ebitda: num(row.ebitda),
    fcff: num(row.fcff),
    pv: num(row.presentValue),
  }));
  return (
    <Card
      title="Free cash flow, year by year"
      note="EBITDA less capex, the change in working capital and tax gives free cash flow; discounting gives what each year is worth today."
    >
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
            <CartesianGrid {...gridProps()} />
            <XAxis dataKey="year" {...axisProps()} />
            <YAxis tickFormatter={fmtAxis} width={64} {...axisProps()} />
            <Tooltip formatter={(v: number | undefined) => money(v ?? 0)} {...tooltipProps()} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Bar dataKey="fcff" name="Free cash flow" fill={seriesColor('cash')} radius={[3, 3, 0, 0]} />
            <Bar dataKey="pv" name="Present value" fill={seriesColor('capital')} radius={[3, 3, 0, 0]} />
            <Line dataKey="ebitda" name="EBITDA" stroke={seriesColor('revenue')} strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

/** From the discounted cash flows to the equity value, as a waterfall. */
function BridgeChart({ computed }: { computed: Row }) {
  // The cash flow's own figures: where a supplied method is the headline (a miner, a
  // holder), the top-level values are that method's, and this waterfall is the forecast's.
  const own = { ...computed, ...((computed.dcf as Row | undefined) ?? {}) };
  const sumPv = num(own.sumOfPVCashFlows);
  const pvTv = num(own.pvTerminalValue);
  const ev = num(own.enterpriseValue);
  const netDebt = num(own.netDebt);
  const equity = num(own.equityValue);
  // A floating bar is a transparent base plus the visible step.
  const steps = [
    { label: 'Cash flows', base: 0, value: sumPv, kind: 'add' },
    { label: 'Terminal value', base: sumPv, value: pvTv, kind: 'add' },
    { label: 'Enterprise value', base: 0, value: ev, kind: 'total' },
    { label: netDebt >= 0 ? 'Net debt' : 'Net cash', base: Math.min(ev, ev - netDebt), value: Math.abs(netDebt), kind: netDebt >= 0 ? 'less' : 'add' },
    { label: 'Equity value', base: 0, value: equity, kind: 'total' },
  ];
  const fill = (kind: string) =>
    kind === 'total' ? 'rgb(var(--c-accent-600))' : kind === 'less' ? seriesColor('cost') : seriesColor('cash');
  return (
    <Card
      title="From cash flows to equity value"
      note="The present value of the forecast years and of the terminal value make the enterprise value; net debt at the valuation date takes it to equity."
    >
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={steps} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
            <CartesianGrid {...gridProps()} />
            {/* Every step labelled: a waterfall read without its labels is just bars. */}
            <XAxis dataKey="label" interval={0} {...axisProps()} />
            <YAxis tickFormatter={fmtAxis} width={64} {...axisProps()} />
            <Tooltip
              formatter={(v: number | undefined, name: string | undefined) =>
                name === 'base' ? [null, null] : [money(v ?? 0), 'Value']
              }
              {...tooltipProps()}
            />
            <Bar dataKey="base" stackId="w" fill="transparent" />
            <Bar dataKey="value" stackId="w" radius={[3, 3, 0, 0]}>
              {steps.map((step) => (
                <Cell key={step.label} fill={fill(step.kind)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

/** The discounted cash flow beside every other method with its inputs supplied. */
function MethodsChart({ computed }: { computed: Row }) {
  const others = ((computed.suppliedMethods as Row[] | undefined) ?? [])
    .filter((m) => typeof m.equityValue === 'number')
    .map((m) => ({ method: String(m.label ?? m.method), value: num(m.equityValue) }));
  const dcf = (computed.dcf as Row | undefined) ?? computed;
  const data = [{ method: 'Discounted cash flow', value: num(dcf.equityValue) }, ...others];
  return (
    <Card
      title="Equity value by method"
      note={
        others.length
          ? 'The discounted cash flow beside each method whose own inputs have been supplied.'
          : 'Only the discounted cash flow so far - supply a method’s inputs on Other methods to compare.'
      }
    >
      <div style={{ height: 48 + data.length * 36 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid {...gridProps()} horizontal={false} />
            <XAxis type="number" tickFormatter={fmtAxis} {...axisProps()} />
            <YAxis type="category" dataKey="method" width={170} {...axisProps()} />
            <Tooltip formatter={(v: number | undefined) => money(v ?? 0)} {...tooltipProps()} />
            <Bar dataKey="value" name="Equity value" radius={[0, 3, 3, 0]}>
              {data.map((row, i) => (
                <Cell key={row.method} fill={i === 0 ? 'rgb(var(--c-accent-600))' : seriesColor('capital')} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

/** Equity value at neighbouring rates - the answer's range, from the engine. */
function SensitivityGrid({ grid, rate, growth }: { grid: Sensitivity; rate: number; growth: number }) {
  const flat = grid.equityValues.flat().filter((v): v is number => typeof v === 'number');
  const lo = Math.min(...flat);
  const hi = Math.max(...flat);
  return (
    <Card
      wide
      title="What a different rate would do"
      note="Equity value at each discount rate (down) and terminal growth rate (across). Every cell is a full revaluation; the outlined one is this valuation."
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-right font-mono text-xs tabular-nums">
          <thead>
            <tr>
              <th className="px-2 py-1.5 text-left font-sans text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Rate \ growth
              </th>
              {grid.growthRates.map((g) => (
                <th key={g} className="px-2 py-1.5 font-semibold text-gray-600">
                  {pct(g)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.discountRates.map((r, i) => (
              <tr key={r}>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-600">{pct(r)}</th>
                {grid.growthRates.map((g, j) => {
                  const value = grid.equityValues[i]?.[j];
                  const share = typeof value === 'number' && hi > lo ? (value - lo) / (hi - lo) : 0.5;
                  const centre = Math.abs(r - rate) < 1e-9 && Math.abs(g - growth) < 1e-9;
                  return (
                    <td
                      key={g}
                      className={`px-2 py-1.5 text-gray-900 ${centre ? 'outline outline-2 -outline-offset-2 outline-accent-600 font-semibold' : ''}`}
                      // Shaded by where the cell sits in the range: the accent at the top.
                      style={{ backgroundColor: `rgb(var(--c-accent-500) / ${(0.06 + share * 0.3).toFixed(3)})` }}
                    >
                      {typeof value === 'number' ? money(value) : '-'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function ValuationCharts({
  computed,
  discountRate,
  terminalGrowthRate,
}: {
  computed: Row;
  discountRate: number;
  terminalGrowthRate: number;
}) {
  const rows = (computed.annualCashFlows as Row[] | undefined) ?? [];
  if (rows.length === 0) {
    return (
      <p className="rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-500">
        No charts yet - this valuation has no forecast cash flows to value.
      </p>
    );
  }
  const grid = computed.sensitivity as Sensitivity | undefined;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <CashFlowChart rows={rows} />
      <BridgeChart computed={computed} />
      {grid && grid.discountRates.length > 0 && (
        <SensitivityGrid grid={grid} rate={discountRate} growth={terminalGrowthRate} />
      )}
      <MethodsChart computed={computed} />
    </div>
  );
}
